import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc as firestoreUpdateDoc, serverTimestamp as firestoreServerTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useFirebase } from './FirebaseContext';
import { useDispatch } from 'react-redux';
import { updateUserData, PlanType, UserData } from '../redux/slices/userSlice';

interface SubscriptionContextType {
  checkSubscription: () => Promise<boolean>;
  upgradePlan: (plan: PlanType) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  renewSubscription: () => Promise<void>;
  isSubscriptionActive: boolean;
  isLoading: boolean;
  error: string | null;
  currentPlan: PlanType | null;
  trialDaysRemaining: number | null;
  subscriptionEndDate: Date | null;
}

interface SubscriptionProviderProps {
  children: ReactNode;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { currentUser } = useFirebase();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<PlanType | null>(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<Date | null>(null);
  const [hasCheckedSubscription, setHasCheckedSubscription] = useState<boolean>(false);

  // Verifica o status da assinatura do usuário
  const checkSubscription = async (): Promise<boolean> => {
    // Se já verificou e não há usuário, retorna falso
    if (!currentUser) {
      setIsSubscriptionActive(false);
      setIsLoading(false);
      return false;
    }

    // Se já está carregando, não inicia outra verificação
    if (isLoading && hasCheckedSubscription) {
      return isSubscriptionActive;
    }

    try {
      setIsLoading(true);
      setError(null);

      if (!currentUser?.uid) {
        setError('Usuário não encontrado');
        setIsLoading(false);
        return false;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const plan = userData.plan as PlanType;
        setCurrentPlan(plan);

        // Verificar se está em período de teste
        if (userData.planFeatures?.isTrial && userData.trialEndDate) {
          const trialEnd = new Date(userData.trialEndDate);
          const now = new Date();
          
          // Calcular dias restantes do trial
          const diffTime = trialEnd.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setTrialDaysRemaining(diffDays > 0 ? diffDays : 0);
          
          // Trial ativo se a data atual for anterior à data de término
          const isTrialActive = now < trialEnd;
          setIsSubscriptionActive(isTrialActive);
          
          // Atualizar o Redux com os dados do usuário
          dispatch(updateUserData(userData));
          setIsLoading(false);
          setHasCheckedSubscription(true);
          return isTrialActive;
        }
        
        // Verificar plano pago
        if (plan !== 'Free') {
          const subscriptionEnd = userData.currentCycleEnd 
            ? new Date(userData.currentCycleEnd) 
            : null;
          
          setSubscriptionEndDate(subscriptionEnd);
          
          if (subscriptionEnd) {
            const now = new Date();
            const isActive = now < subscriptionEnd;
            setIsSubscriptionActive(isActive);
            
            // Atualizar o Redux com os dados do usuário
            dispatch(updateUserData(userData));
            setIsLoading(false);
            setHasCheckedSubscription(true);
            return isActive;
          }
        }
        
        // Plano gratuito ou sem data de término definida
        const isFreeActive = plan === 'Free';
        setIsSubscriptionActive(isFreeActive);
        
        // Atualizar o Redux com os dados do usuário
        dispatch(updateUserData(userData));
        setIsLoading(false);
        setHasCheckedSubscription(true);
        return isFreeActive;
      } else {
        // Criar um novo documento de usuário com plano gratuito e período de teste
        const trialDays = 15; // 15 dias de teste
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + trialDays);
        
        const newUserData: Partial<UserData> = {
          name: currentUser.displayName || 'Usuário',
          email: currentUser.email || undefined,
          plan: 'Free' as PlanType,
          trialStartDate: new Date().toISOString(),
          trialEndDate: trialEndDate.toISOString(),
          planFeatures: {
            maxLeads: 10,
            maxProducts: 10,
            whatsappBot: false,
            emailMarketing: false,
            chatIA: true,
            multipleUsers: false,
            customReports: false,
            support: 'basic' as 'basic',
            isTrial: true,
            trialDays: trialDays,
            price: 0,
            description: 'Plano gratuito com recursos limitados'
          },
          createdAt: firestoreServerTimestamp(),
          updatedAt: firestoreServerTimestamp()
        };
        
        await setDoc(userRef, newUserData);
        
        setCurrentPlan('Free');
        setTrialDaysRemaining(trialDays);
        setIsSubscriptionActive(true);
        
        // Atualizar o Redux com os dados do usuário
        dispatch(updateUserData(newUserData));
        setIsLoading(false);
        setHasCheckedSubscription(true);
        return true;
      }
    } catch (err) {
      console.error('Erro ao verificar assinatura:', err);
      setError('Erro ao verificar status da assinatura. Tente novamente.');
      setIsLoading(false);
      setHasCheckedSubscription(true);
      return false;
    }
  };

  // Atualiza o plano do usuário
  const upgradePlan = async (plan: PlanType): Promise<void> => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      setIsLoading(true);
      setError(null);

      if (!currentUser?.uid) {
        setError('Usuário não encontrado');
        setIsLoading(false);
        return;
      }

      // Definir recursos do plano com base no tipo
      const planFeatures = getPlanFeatures(plan);
      
      // Definir data de término do ciclo atual (1 mês a partir de agora)
      const cycleEndDate = new Date();
      cycleEndDate.setMonth(cycleEndDate.getMonth() + 1);
      
      const userRef = doc(db, 'users', currentUser.uid);
      await firestoreUpdateDoc(userRef, {
        plan,
        planFeatures,
        currentCycleEnd: cycleEndDate.toISOString(),
        updatedAt: firestoreServerTimestamp()
      });
      
      // Atualizar estados locais
      setCurrentPlan(plan);
      setIsSubscriptionActive(true);
      setSubscriptionEndDate(cycleEndDate);
      
      // Buscar dados atualizados para o Redux
      const updatedUserSnap = await getDoc(userRef);
      if (updatedUserSnap.exists()) {
        dispatch(updateUserData(updatedUserSnap.data()));
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Erro ao atualizar plano:', err);
      setError('Erro ao atualizar plano. Tente novamente.');
      setIsLoading(false);
      throw err;
    }
  };

  // Cancela a assinatura do usuário
  const cancelSubscription = async (): Promise<void> => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      setIsLoading(true);
      setError(null);

      if (!currentUser?.uid) {
        setError('Usuário não encontrado');
        setIsLoading(false);
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Atualizar para plano gratuito
      const freePlanFeatures = getPlanFeatures('Free');
      
      await firestoreUpdateDoc(userRef, {
        plan: 'Free',
        planFeatures: freePlanFeatures,
        currentCycleEnd: null,
        updatedAt: firestoreServerTimestamp()
      });
      
      // Atualizar estados locais
      setCurrentPlan('Free');
      setIsSubscriptionActive(true); // Plano Free é sempre ativo
      setSubscriptionEndDate(null);
      
      // Buscar dados atualizados para o Redux
      const updatedUserSnap = await getDoc(userRef);
      if (updatedUserSnap.exists()) {
        dispatch(updateUserData(updatedUserSnap.data()));
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Erro ao cancelar assinatura:', err);
      setError('Erro ao cancelar assinatura. Tente novamente.');
      setIsLoading(false);
      throw err;
    }
  };

  // Renova a assinatura do usuário
  const renewSubscription = async (): Promise<void> => {
    if (!currentUser || !currentPlan) {
      throw new Error('Usuário não autenticado ou plano não definido');
    }

    try {
      setIsLoading(true);
      setError(null);

      if (!currentUser?.uid) {
        setError('Usuário não encontrado');
        setIsLoading(false);
        return;
      }

      // Definir nova data de término do ciclo (1 mês a partir de agora)
      const newCycleEndDate = new Date();
      newCycleEndDate.setMonth(newCycleEndDate.getMonth() + 1);
      
      const userRef = doc(db, 'users', currentUser.uid);
      await firestoreUpdateDoc(userRef, {
        currentCycleEnd: newCycleEndDate.toISOString(),
        updatedAt: firestoreServerTimestamp()
      });
      
      // Atualizar estados locais
      setIsSubscriptionActive(true);
      setSubscriptionEndDate(newCycleEndDate);
      
      // Buscar dados atualizados para o Redux
      const updatedUserSnap = await getDoc(userRef);
      if (updatedUserSnap.exists()) {
        const userData = updatedUserSnap.data();
        const newUserData: any = {
          ...userData,
          name: userData.name || '',
          email: userData.email || undefined,
          plan: userData.plan,
          trialStartDate: userData.trialStartDate,
          trialEndDate: userData.trialEndDate,
          planFeatures: userData.planFeatures,
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt
        };
        dispatch(updateUserData(newUserData));
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Erro ao renovar assinatura:', err);
      setError('Erro ao renovar assinatura. Tente novamente.');
      setIsLoading(false);
      throw err;
    }
  };

  // Função auxiliar para obter recursos do plano com base no tipo
  const getPlanFeatures = (plan: PlanType) => {
    switch (plan) {
      case 'Basic':
        return {
          maxLeads: 100,
          maxProducts: 50,
          whatsappBot: true,
          emailMarketing: true,
          chatIA: true,
          multipleUsers: false,
          customReports: false,
          support: 'basic' as 'basic',
          isTrial: false,
          trialDays: 0,
          price: 59.90,
          description: 'Plano básico para pequenos negócios'
        };
      case 'Pro':
        return {
          maxLeads: 500,
          maxProducts: 200,
          whatsappBot: true,
          emailMarketing: true,
          chatIA: true,
          multipleUsers: true,
          customReports: true,
          support: 'priority' as 'priority',
          isTrial: false,
          trialDays: 0,
          price: 99.90,
          description: 'Plano profissional com recursos avançados'
        };
      case 'Free':
      default:
        return {
          maxLeads: 10,
          maxProducts: 10,
          whatsappBot: false,
          emailMarketing: false,
          chatIA: true,
          multipleUsers: false,
          customReports: false,
          support: 'basic' as 'basic',
          isTrial: false,
          trialDays: 0,
          price: 0,
          description: 'Plano gratuito com recursos limitados'
        };
    }
  };

  // Verificar assinatura quando o usuário mudar
  useEffect(() => {
    if (currentUser && !hasCheckedSubscription) {
      // Inicializar com valores padrão para evitar redirecionamentos indesejados
      setIsSubscriptionActive(true); // Considerar ativo por padrão
      setCurrentPlan('Free');
      setIsLoading(false);
      
      // Verificar assinatura em segundo plano
      checkSubscription().then(() => {
        console.log('Verificação de assinatura concluída');
      }).catch(err => {
        console.error('Erro ao verificar assinatura:', err);
      });
    } else if (!currentUser) {
      setIsSubscriptionActive(false);
      setCurrentPlan(null);
      setTrialDaysRemaining(null);
      setSubscriptionEndDate(null);
      setIsLoading(false);
      setHasCheckedSubscription(false);
    }
  }, [currentUser, hasCheckedSubscription]);

  const value = {
    checkSubscription,
    upgradePlan,
    cancelSubscription,
    renewSubscription,
    isSubscriptionActive,
    isLoading,
    error,
    currentPlan,
    trialDaysRemaining,
    subscriptionEndDate
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
